import React from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { getElementId, getReactions, getLabels } from '../../../common/elements/utils/elementPropertyUtils';
import { length } from '../../../common/utils/immutableHelper';
import { getCurrentUserId } from '../../user/currentUserSelector';
import { getGridSize } from '../../utils/grid/gridSizeSelector';
import { getUniqueReactions } from '../reactions/reactionUtil';
import { canWrite, canGiveFeedback } from '../../../common/permissions/permissionUtil';
import { selectLabelsByIds } from '../labels/labelSelectors';

// Components
import ElementReactionPillContainer from '../reactions/ElementReactionPillContainer';
import LabelElementPillsContainer from '../labels/LabelElementPillsContainer';
import ElementReactionAddButton from '../reactions/ElementReactionAddButton';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';

import './PillsPanel.scss';

const mapStateToProps = createStructuredSelector({
    gridSize: getGridSize,
    currentUserId: getCurrentUserId,
});

type Props = {
    element: ImMNElement;
    elementId?: string;
    reactions?: object[] | object;
    currentUserId: string;
    gridSize: number;
    permissions: number;
    filterQuery?: string;
    showAddReactionButton?: boolean;
};

type Reaction = {
    emojiCode: string;
    userIds: string[];
};

const ElementReactionList = (props: Props) => {
    const {
        element,
        elementId: _elementId,
        reactions: _reactions,
        currentUserId,
        gridSize,
        permissions,
        filterQuery,
        showAddReactionButton,
    } = props;
    const elementId = _elementId || getElementId(element);
    const reactions = _reactions || getReactions(element) || [];
    const reactionCount = length(reactions);

    const uniqueReactionsList: Reaction[] = React.useMemo(
        () => Object.values(getUniqueReactions(reactions)),
        [reactions],
    );

    const elementLabels = useSelector(selectLabelsByIds(getLabels(element)));
    const labelCount = length(elementLabels);

    if (!reactionCount && !labelCount) return null;

    const userCanWriteReactions = canWrite(permissions) || canGiveFeedback(permissions);

    return (
        <div className="ElementReactions">
            <div className="reaction-button-container">
                <LabelElementPillsContainer
                    labels={elementLabels}
                    size="sm"
                    filterQuery={filterQuery}
                    elementId={elementId}
                />
                {uniqueReactionsList.map(({ emojiCode, userIds }) => (
                    <ElementReactionPillContainer
                        elementId={elementId}
                        key={emojiCode}
                        disabled={!userCanWriteReactions}
                        emojiCode={emojiCode}
                        userIds={userIds}
                        currentUserId={currentUserId}
                        gridSize={gridSize}
                        permissions={permissions}
                    />
                ))}
                {showAddReactionButton && userCanWriteReactions && !!reactionCount && (
                    <ElementReactionAddButton elementId={elementId} />
                )}
            </div>
        </div>
    );
};

ElementReactionList.propTypes = {
    currentUserId: PropTypes.string,
    elementId: PropTypes.string,
    permissions: PropTypes.number,
    gridSize: PropTypes.number,
    reactions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    element: PropTypes.object,
    toggleReaction: PropTypes.func,
};

export default connect(mapStateToProps)(ElementReactionList);
